import { useMemo } from 'react';
import { type PublicClient, type WalletClient, getPublicClient } from '@wagmi/core';
import { useWalletClient } from 'wagmi';
import type { Provider } from '@ethersproject/providers';
import { providers, Signer } from 'ethers';
import { type HttpTransport } from 'viem';

export function publicClientToProvider(publicClient: PublicClient) {
	const { chain, transport } = publicClient;
	const network = {
		chainId: chain.id,
		name: chain.name,
		ensAddress: chain.contracts?.ensRegistry?.address,
	};
	if (transport.type === 'fallback')
		return new providers.FallbackProvider(
			(transport.transports as ReturnType<HttpTransport>[]).map(
				({ value }) =>
					new providers.JsonRpcProvider(value?.url, network),
			),
		);
	return new providers.JsonRpcProvider(transport.url, network);
}

/** Action to convert a viem Public Client to an ethers.js Provider. */
export const useEthersProvider = (): Provider | undefined => {
	const publicClient = getPublicClient();
	return publicClientToProvider(publicClient);
}

export function walletClientToSigner(walletClient: WalletClient) {
	const { account, chain, transport } = walletClient;
	const network = {
		chainId: chain.id,
		name: chain.name,
		ensAddress: chain.contracts?.ensRegistry?.address,
	};
	const provider = new providers.Web3Provider(transport, network);
	const signer = provider.getSigner(account.address);
	return signer;
}

/** Action to convert a viem Wallet Client to an ethers.js Signer. */
export const useEthersSigner = () => {
  const { data: walletClient, isLoading, error } = useWalletClient();
  const signer = useMemo(() => {
    if (walletClient) return walletClientToSigner(walletClient);
    return undefined;
  }, [walletClient]);

  return { data: signer, isLoading, error } as {
    data?: Signer;
    isLoading?: boolean;
    error?: Error | null;
  };
};